import WarningDiamondIcon from 'components/icons/WarningDiamondIcon';
import Tooltip from 'components/Tooltip';
import Image from 'next/image';
import InlineTooltip from 'components/InlineTooltip';
import HotspotTooltip from 'components/HotspotTooltip';
import UILink from 'components/ui/UILink';
const cardImages = [
  {
    src: '/part-a-timeline-hospital.svg',
    alt: 'Medicare Part A Hospital costs chart',
    caption:
      'Infographic shows how hospital costs are covered with Medicare Part A. From Days 1 through 60, Daniel pays a one-time Part A deductible of $1,736. From Days 61 to 90, he pays a $434 per day copay, for a total of $13,020 for 30 days. From Days 91 to 150, he pays a $868 per day copay, for a total of $52,080 for 60 days. These days are called Lifetime Reserve Days, which can only be used once in a lifetime. From Day 151 on, there is no more Medicare coverage, and Daniel pays the full cost, which can be thousands of dollars.',
    width: 1060,
    height: 841,
  },
  {
    src: '/part-b-donut-step-1.svg',
    alt: 'Medicare Part B specialist visit costs chart',
    caption:
      "Infographic showing the cost of a visit to an orthopedic specialist for a pain in Sarah's wrist with Medicare Part B. The total cost for the visit before insurance is $502. Sarah first pays a $283 deductible. After the deductible, $219 remains. Sarah pays 20% coinsurance of that amount ($44). Medicare Part B pays the remaining 80% coinsurance ($175).",
    width: 1060,
    height: 597,
  },
  {
    src: '/part-b-donut-step-2.svg',
    alt: 'Medicare Part B MRI costs chart',
    caption:
      "Infographic showing the cost of an MRI for Sarah's wrist with Medicare Part B. The total cost of the MRI before insurance is $3,000. Sarah pays 20% coinsurance ($600). Medicare Part B pays the remaining 80% coinsurance ($2,400).",
    width: 1060,
    height: 527,
  },
  {
    src: '/part-b-donut-step-3.svg',
    alt: 'Medicare Part B outpatient surgery costs chart',
    caption:
      "Infographic showing the cost of outpatient surgery to treat Sarah's wrist with Medicare Part B. The total cost of the surgery before insurance is $5,750. Medicare Part B pays for 80% coinsurance ($4,000). Sarah pays the 15% excess charge that is not covered by Medicare ($750). She also pays 20% coinsurance on the remainder ($1,000)",
    width: 1060,
    height: 597,
  },
];

const PartABExplainer = () => {
  return (
    <section className="lfe-page-container coverage-explainer">
      {/* Header */}
      <div className="coverage-explainer-header">
        <h2>How Does Medicare Cover My Costs?</h2>
        <p>
          Let&rsquo;s take a look at some examples of how Parts A and B handle
          your health care costs, and then the ways Medicare Supplement, Part D
          and Medicare Advantage each provide additional coverage.
        </p>
      </div>

      {/* Part A Card */}
      <div className="lfe-card example-card">
        <div className="lfe-card-header">
          <h2 className="lead-in">
            <b>Part A:</b> Hospital Insurance
          </h2>
          <h3>Example of How Part A Covers a Hospital Stay (in 2026)</h3>
          <p className="centered">
            Part A coverage details are taken directly from{' '}
            <UILink
              textType="body"
              size="large"
              label="cms.gov"
              href="https://www.cms.gov/"
              external
            />
            .
          </p>
        </div>

        <div className="lfe-card-content">
          <div className="example-explainer">
            <p>In this example...</p>
            <p className="example-content">
              Daniel is admitted to the hospital for cancer treatment. His
              treatment requires hospitalization for over 150 days.
            </p>
          </div>

          <div className="itemized-costs-card">
            <div className="itemized-costs-content">
              <table>
                <caption>Part A Coverage Details</caption>
                <tbody>
                  <tr>
                    <th scope="row">
                      Monthly{' '}
                      <InlineTooltip text="Premium" tooltipText={'premium'} />
                    </th>
                    <td>
                      <b>$0</b>
                      <Tooltip
                        horizontalAlign="left"
                        color="blue"
                        subject="Part A premium"
                      >
                        Most people don&apos;t have a Part A premium. It&apos;s
                        $0 if you&apos;ve worked a minimum of 10 years.
                      </Tooltip>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="Deductible"
                        tooltipText={'deductible'}
                      />{' '}
                      per <b>benefit period:</b>
                    </th>
                    <td>
                      <b>$1,736</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="rowgroup">
                      <InlineTooltip text="Copay" tooltipText={'copay'} /> per{' '}
                      <b>benefit period:</b>
                    </th>
                    <td>
                      Days 61-90: <b>$434</b>
                      <br></br>
                      Days 91-150: <b>$868</b>
                      <br></br>
                      Days 151+: <b>Not covered</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="Out-of-Pocket Max:"
                        tooltipText={'oopm'}
                      />
                    </th>
                    <td>None</td>
                  </tr>
                </tbody>
              </table>
              <div className="note">
                <WarningDiamondIcon />
                <span>
                  A <b>benefit period</b> starts the first day you&apos;re
                  admitted into a hospital or skilled nursing facility. It lasts
                  until you&apos;ve been released and stay out for 60 days.
                  <Tooltip
                    horizontalAlign="left"
                    color="blue"
                    subject="benefit period"
                  >
                    You can have multiple benefit periods in one year. If you do
                    have more than one benefit period, the deductible has to be
                    paid for each one.
                  </Tooltip>
                </span>
              </div>
            </div>
          </div>

          {/* Timeline Chart */}
          <figure className="annotated-image">
            <Image
              src={cardImages[0].src}
              alt={cardImages[0].alt}
              width={cardImages[0].width}
              height={cardImages[0].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[0].caption}
            </figcaption>

            <HotspotTooltip
              x={9}
              y={53}
              text="deductible"
              tooltipText="deductible"
            />

            <HotspotTooltip
              x={12}
              y={80}
              text="deductible"
              tooltipText="deductible"
            />

            <HotspotTooltip x={41} y={44} text="copay" tooltipText="copay" />

            <HotspotTooltip x={65} y={34} text="copay" tooltipText="copay" />
          </figure>

          <div className="itemized-costs-card small">
            <div className="itemized-costs-content">
              <table>
                <caption>Part A Example Total Costs</caption>
                <tbody>
                  <tr>
                    <th scope="row">
                      Yearly{' '}
                      <InlineTooltip text="Premium" tooltipText={'premium'} />
                    </th>
                    <td>
                      <b>$0</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="Deductible"
                        tooltipText={'deductible'}
                      />
                    </th>
                    <td>
                      <b>$1,736</b> per <b>benefit period:</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="rowgroup">
                      <InlineTooltip text="Copay" tooltipText={'copay'} /> Total
                      for Days 61-150:
                    </th>
                    <td>
                      <b>$65,100</b>
                    </td>
                  </tr>
                  <tr className="total">
                    <th scope="row">
                      <b>Overall Total for 150 days:</b>
                    </th>
                    <td>
                      <span className="total">
                        <b>$66,836</b>
                      </span>
                      <br></br>(then no more coverage)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Part B Card */}
      <div className="lfe-card example-card part-b">
        <div className="lfe-card-header">
          <h2 className="lead-in part-b">
            <b>Part B:</b> Medical Insurance
          </h2>
          <h3>
            Example of How Part B Covers a Series of Medical Services (in 2026)
          </h3>
          <p>
            (Part B coverage details are taken directly from{' '}
            <UILink
              textType="body"
              size="large"
              label="cms.gov"
              href="https://www.cms.gov/"
              external
            />
            . Costs for services based on national averages according to MDsave
            and{' '}
            <UILink
              textType="body"
              size="large"
              label="Medicare.gov"
              href="https://www.medicare.gov/procedure-price-lookup/cost/70553/"
              external
            />
            . Actual costs for provider visits and services may vary in your
            area)
          </p>
        </div>
        <div className="lfe-card-content">
          <div className="example-explainer">
            <p>In this example...</p>
            <ol className="example-content">
              <li>Sarah visits her orthopedist for a pain in her wrist.</li>
              <li>
                {
                  'The doctor orders an MRI, confirming she needs a total shoulder replacement'
                }
              </li>
              <li>An outpatient surgery is performed as treatment.</li>
            </ol>
          </div>

          <div className="itemized-costs-card part-b">
            <div className="itemized-costs-content">
              <table>
                <caption>Part B Coverage Details</caption>
                <tbody>
                  <tr>
                    <th scope="row">
                      Monthly{' '}
                      <InlineTooltip text="Premium" tooltipText={'premium'} />:
                    </th>
                    <td>
                      <span className="row-with-tooltip">
                        <b>$202.90</b>&nbsp;(minimum)
                        <Tooltip
                          horizontalAlign="left"
                          color="blue"
                          subject="Part B premium"
                        >
                          The Part B premium is based on how much you earned two
                          years earlier. It increases for higher incomes,
                          starting at over $109,000 for those filing
                          individually, and over $218,000 for those filing
                          jointly).
                        </Tooltip>
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="Deductible"
                        tooltipText={'deductible'}
                      />{' '}
                      (only paid once per plan year):
                    </th>
                    <td>
                      <b>$283</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="Coinsurance"
                        tooltipText={'coinsurance'}
                      />
                      :
                    </th>
                    <td>
                      <b>20%</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="Out-of-Pocket Max"
                        tooltipText={'oopm'}
                      />
                      :
                    </th>
                    <td>
                      <b>None</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Donut charts */}
          <figure className="annotated-image">
            <Image
              src={cardImages[1].src}
              alt={cardImages[1].alt}
              width={cardImages[1].width}
              height={cardImages[1].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[1].caption}
            </figcaption>
            <HotspotTooltip
              x={35}
              y={21}
              text="deductible"
              tooltipText="deductible"
            />
            <HotspotTooltip
              x={68}
              y={46}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip
              x={66}
              y={51}
              text="deductible"
              tooltipText="deductible"
              direction="left"
            />
            <HotspotTooltip
              x={67}
              y={67}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip
              x={65}
              y={72}
              text="deductible"
              tooltipText="deductible"
              direction="left"
            />
          </figure>
          <figure className="annotated-image">
            <Image
              src={cardImages[2].src}
              alt={cardImages[2].alt}
              width={cardImages[2].width}
              height={cardImages[2].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[2].caption}
            </figcaption>
            <HotspotTooltip
              x={64}
              y={34}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip
              x={64}
              y={68}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip
              x={57}
              y={74}
              text="deductible"
              tooltipText="deductible"
              direction="left"
            />
          </figure>
          <figure className="annotated-image">
            <Image
              src={cardImages[3].src}
              alt={cardImages[3].alt}
              width={cardImages[3].width}
              height={cardImages[3].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[3].caption}
            </figcaption>
            <HotspotTooltip
              x={38}
              y={25}
              text="coinsurance"
              tooltipText="coinsurance"
            />
            <HotspotTooltip
              x={60}
              y={41}
              text="15% excess charge"
              tooltipText="excessCharge"
              direction="left"
            />
            <HotspotTooltip
              x={65}
              y={72}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
          </figure>

          <div className="itemized-costs-card small part-b">
            <div className="itemized-costs-content">
              <table>
                <caption>Part B Example Total Costs</caption>
                <tbody>
                  <tr>
                    <th scope="row">
                      Yearly{' '}
                      <InlineTooltip text="Premium" tooltipText={'premium'} />:
                    </th>
                    <td>
                      <b>{'$2,435'}</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="Deductible"
                        tooltipText={'deductible'}
                      />
                      :
                    </th>
                    <td>
                      <b>$283</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="Coinsurance"
                        tooltipText={'coinsurance'}
                      />{' '}
                      total:
                    </th>
                    <td>
                      <b>{'$2,847'}</b>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <InlineTooltip
                        text="15% Excess Charges"
                        tooltipText={'excessCharge'}
                      />
                    </th>
                    <td>
                      <b>{'$2,394'}</b>
                    </td>
                  </tr>
                  <tr className="total">
                    <th scope="row">
                      <b>Overall Total:</b>
                    </th>
                    <td>
                      <b>{'$7,959'}</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartABExplainer;
